"use client";

import React, { useState } from "react";
import { TileCodeBlock } from "@/components/TileContent";
import {
  CheckCircle,
  Play,
  AlertTriangle,
  Wand2,
  Copy,
  Loader2,
  Code2,
  Info,
} from "lucide-react";

type Finding = { type: "error" | "success" | "warning"; message: string };

interface PresetData {
  label: string;
  snippet: string;
  optimizedSnippet: string;
  results: Finding[];
}

const PRESETS: Record<string, PresetData> = {
  form: {
    label: "Formular ohne Label",
    snippet: '<div>\n  <span id="name-lbl">Name:</span>\n  <input type="text" placeholder="Name">\n</div>',
    optimizedSnippet: '<div>\n  <label for="user-name" id="name-lbl">Name:</label>\n  <input id="user-name" type="text" placeholder="Name" aria-labelledby="name-lbl">\n</div>',
    results: [
      {
        type: "error",
        message:
          "Formular-Eingabefeld ohne programmatische Verknüpfung (<label for='...'>). Screenreader können das Feld sonst nicht verlässlich benennen (WCAG 1.3.1 / 4.1.2).",
      },
      {
        type: "success",
        message:
          "Echtes <label>-Element mit for-Attribut ergänzt und auf die ID des Inputs verwiesen.",
      },
    ],
  },
  img: {
    label: "Klickbares <img>",
    snippet: '<img src="profilbild.png" class="avatar" onclick="zoomIn()">',
    optimizedSnippet: '<button type="button" class="avatar-btn" onclick="zoomIn()" aria-label="Profilbild vergrößern">\n  <img src="profilbild.png" class="avatar" alt="Profilbild von Lukas">\n</button>',
    results: [
      {
        type: "error",
        message:
          "Klickbares <img> ohne interaktives Elternelement (<button>). Per Tastatur (Tab/Enter) nicht erreichbar und ohne Tastatur-Eventhandler (WCAG 2.1.1).",
      },
      {
        type: "warning",
        message:
          "Fehlendes alt-Attribut ergänzt. Bildbeschreibungen müssen den visuellen Kontext beschreiben (WCAG 1.1.1).",
      },
      {
        type: "success",
        message:
          "In einen semantischen <button> eingebettet – vollständige Tastaturbedienung und Fokusring wiederhergestellt.",
      },
    ],
  },
  button: {
    label: "Fake <div> Button",
    snippet: '<div class="btn" onclick="submit()">Absenden</div>',
    optimizedSnippet: '<button type="submit" class="btn" onclick="submit()">Absenden</button>',
    results: [
      {
        type: "error",
        message:
          "Fake-Button aus <div> mit onclick statt nativem <button> (WCAG 4.1.2). Es fehlen Fokusierbarkeit, Tastatur-Trigger (Enter/Space) und Screenreader-Semantik.",
      },
      {
        type: "success",
        message:
          "Durch natives <button>-Element ersetzt – automatisch per Tab ansteuerbar, mit Enter/Space auslösbar und mit nativer ARIA-Rolle.",
      },
    ],
  },
};

export default function AccessibilityPlayground() {
  const [error, setError] = useState("");
  const [snippet, setSnippet] = useState(PRESETS.form.snippet);
  const [optimizedSnippet, setOptimizedSnippet] = useState<string | null>(
    PRESETS.form.optimizedSnippet,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<Finding[] | null>(
    PRESETS.form.results,
  );

  const selectPreset = (key: keyof typeof PRESETS) => {
    const p = PRESETS[key];
    setSnippet(p.snippet);
    setOptimizedSnippet(p.optimizedSnippet);
    setAnalysisResult(p.results);
    setError("");
  };

  const analyzeAndOptimize = async () => {
    if (!snippet.trim()) return;

    if (typeof navigator !== "undefined" && !navigator.onLine) {
      setError(
        "Du bist offline. Für eigene HTML-Analysen wird eine Internetverbindung zu Gemini benötigt. Die Presets oben funktionieren offline.",
      );
      return;
    }

    setIsLoading(true);
    setError("");
    setAnalysisResult(null);
    setOptimizedSnippet(null);

    try {
      const response = await fetch("/api/accessibility/optimize", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ snippet }),
        signal: AbortSignal.timeout(35000),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Die Analyse ist fehlgeschlagen.");
      }
      setAnalysisResult(data.results);
      setOptimizedSnippet(data.optimizedSnippet);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Die Analyse ist fehlgeschlagen.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (optimizedSnippet) {
      void navigator.clipboard.writeText(optimizedSnippet);
    }
  };

  return (
    <div className="space-y-6">
      <p className="text-xs text-neutral-500">
        Gemini · Eigener HTML-Code wird zur Optimierung an Google gesendet. Die Presets werden lokal ausgewertet.
      </p>

      {error && (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}

      {/* 1. Header & Presets */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-neutral-200/80 bg-neutral-100/60 p-3 dark:border-neutral-800 dark:bg-neutral-950/60 sm:p-4">
        <div className="text-xs font-medium text-neutral-600 dark:text-neutral-400 sm:text-sm">
          Typischen a11y-Fehler wählen oder eigenen Code testen:
        </div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(PRESETS).map(([key, p]) => (
            <button
              key={key}
              onClick={() => selectPreset(key)}
              className="shadow-2xs dark:hover:bg-neutral-850 rounded-lg border border-neutral-200 bg-white px-2.5 py-1 text-xs font-medium text-neutral-700 transition-all hover:border-neutral-300 hover:bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-300"
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* 2. Side-by-Side Code Comparison */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* INPUT */}
        <div className="flex flex-col space-y-2">
          <label className="flex items-center gap-2 text-xs font-semibold text-neutral-700 dark:text-neutral-300 sm:text-sm">
            <Code2 className="h-4 w-4 text-neutral-500" />
            <span>Problematischer HTML-Code</span>
          </label>
          <textarea
            className="shadow-2xs h-44 w-full resize-y rounded-xl border border-neutral-200 bg-white p-3.5 font-mono text-xs leading-relaxed transition-colors focus:border-neutral-500 focus:outline-none focus:ring-2 focus:ring-neutral-500/20 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-100 dark:focus:border-neutral-500 sm:text-sm"
            value={snippet}
            onChange={(e) => setSnippet(e.target.value)}
            placeholder="Füge hier fehlerhaften HTML-Code ein..."
          />
        </div>

        {/* OUTPUT */}
        <div className="flex flex-col space-y-2">
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-xs font-semibold text-neutral-700 dark:text-neutral-300 sm:text-sm">
              <Wand2 className="h-4 w-4 text-emerald-500" />
              <span>Zugänglicher Best-Practice Code</span>
            </label>
            {optimizedSnippet && (
              <button
                onClick={copyToClipboard}
                title="Code kopieren"
                className="inline-flex items-center gap-1 text-xs font-medium text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white"
              >
                <Copy className="h-3.5 w-3.5" />
                <span>Kopieren</span>
              </button>
            )}
          </div>

          <div className="relative flex h-44 overflow-hidden rounded-xl border border-neutral-200 bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-950/80">
            {optimizedSnippet ? (
              <div className="w-full overflow-auto">
                <TileCodeBlock language="html" title="Zugänglicher Code">
                  {optimizedSnippet}
                </TileCodeBlock>
              </div>
            ) : (
              <div className="flex h-full w-full flex-col items-center justify-center p-4 text-center text-xs text-neutral-400 dark:text-neutral-500 sm:text-sm">
                <Info className="mb-2 h-5 w-5 opacity-40" />
                Klicke auf &quot;Code prüfen &amp; optimieren&quot;, um den semantisch korrigierten Code zu sehen.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Action Button */}
      <div className="flex justify-center pt-1">
        <button
          onClick={analyzeAndOptimize}
          disabled={isLoading || !snippet}
          className="font-heading inline-flex items-center justify-center gap-2 rounded-xl bg-neutral-900 px-6 py-2.5 text-sm font-bold tracking-tight text-white shadow-md transition-all hover:bg-neutral-800 active:scale-95 disabled:cursor-not-allowed disabled:bg-neutral-300 dark:bg-white dark:text-neutral-900 dark:hover:bg-neutral-200 dark:disabled:bg-neutral-800 dark:disabled:text-neutral-600"
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Play className="h-4 w-4 fill-current" />
          )}
          <span>
            {isLoading ? "Analysiere Code..." : "Eigenen Code prüfen & optimieren"}
          </span>
        </button>
      </div>

      {/* 3. Feedback Sektion */}
      {analysisResult && (
        <div className="mt-6 space-y-3">
          <h4 className="font-heading text-xs font-bold uppercase tracking-wider text-neutral-500">
            Accessibility-Prüfbericht (WCAG 2.1 / BITV 2.0)
          </h4>
          <div className="grid gap-3 sm:grid-cols-2">
            {analysisResult.map((res, idx) => {
              const isError = res.type === "error";
              const isWarning = res.type === "warning";

              const styles = isError
                ? "border-red-500/30 bg-red-50/70 text-red-900 dark:border-red-500/30 dark:bg-red-950/20 dark:text-red-300"
                : isWarning
                  ? "border-amber-500/30 bg-amber-50/70 text-amber-900 dark:border-amber-500/30 dark:bg-amber-950/20 dark:text-amber-300"
                  : "border-emerald-500/30 bg-emerald-50/70 text-emerald-900 dark:border-emerald-500/30 dark:bg-emerald-950/20 dark:text-emerald-300";

              return (
                <div
                  key={idx}
                  className={`flex items-start gap-3 rounded-xl border p-3.5 text-xs sm:p-4 sm:text-sm ${styles}`}
                >
                  {isError || isWarning ? (
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                  ) : (
                    <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                  )}
                  <span className="font-sans leading-relaxed">
                    {res.message}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
